import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

export default function RoofingCalc() {
  const [tab, setTab] = useState('roof');
  const [unit, setUnit] = useState('ft');
  const UN = { ft:1, in:1/12, m:3.28084, cm:0.0328084, yd:3 };
  const UN_LABEL = { ft:'ft — foot', in:'in — inch', m:'m — meter', cm:'cm — centimeter', yd:'yd — yard' };
  const [length, setLength] = useState(40);
  const [width, setWidth] = useState(30);
  const [pitch, setPitch] = useState(6);
  const [angleDeg, setAngleDeg] = useState(30);
  const [pitchInput, setPitchInput] = useState(6);

  // Material calc
  const [materialCost, setMaterialCost] = useState(30); // per bundle
  const [wasteFactor, setWasteFactor] = useState(10);

  // Convert L/W to feet
  const Lft = Number(length) * UN[unit], Wft = Number(width) * UN[unit];
  const c = useMemo(() => {
    const slope = Math.sqrt(1 + Math.pow(pitch/12, 2));
    const area = Lft * Wft * slope;
    const squares = area/100;
    const bundles = Math.ceil(squares * 3 * (1 + Number(wasteFactor)/100));
    const rise = Wft/2 * pitch/12;
    const rafter = Math.sqrt((Wft/2)**2 + rise**2);
    const angle = Math.atan(pitch/12) * 180/Math.PI;
    return { area, squares, bundles, rise, rafter, angle, slope };
  }, [Lft, Wft, pitch, wasteFactor]);

  const pitchFromAngle = Math.tan(Number(angleDeg) * Math.PI/180) * 12;
  const angleFromPitch = Math.atan(Number(pitchInput)/12) * 180/Math.PI;

  return (
    <CalcShell look="cnet"
      title="Roofing Calculator"
      subtitle="Estimate roof area, squares of shingles, bundles, or convert between pitch and roof angle."
      about={<>
        <p><b>Roof pitch</b> is the vertical rise per 12 inches of horizontal run. A 6/12 pitch = 6" of rise for every 12" of run.</p>
        <p>One <b>roofing square</b> = 100 sq ft of surface. Standard 3-tab shingles use ~3 bundles per square.</p>
      </>}
    >
      <div className="flex gap-2 border-b mb-4 flex-wrap">
        {[['roof','Roofing Area'],['material','Roofing Material'],['pitch','Roof Pitch'],['angle','Roof Angle']].map(([v,l]) => (
          <button key={v} className={`px-4 py-2 text-sm font-semibold ${tab===v?'border-b-2 border-blue-600 text-blue-700':'text-slate-500'}`} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>

      {(tab === 'roof' || tab === 'material') && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Length</label><input type="number" value={length} onChange={e => setLength(e.target.value)}/></div>
            <div className="field"><label>Width</label><input type="number" value={width} onChange={e => setWidth(e.target.value)}/></div>
          </div>
          <div className="field"><label>Unit</label>
            <select value={unit} onChange={e => setUnit(e.target.value)}>{Object.keys(UN).map(u => <option key={u} value={u}>{UN_LABEL[u]}</option>)}</select>
          </div>
          <div className="field"><label>Pitch (rise per 12")</label><input type="number" step="0.5" value={pitch} onChange={e => setPitch(+e.target.value || 0)}/></div>

          {tab === 'material' && (<>
            <div className="field"><label>Waste Factor (%)</label><input type="number" value={wasteFactor} onChange={e => setWasteFactor(e.target.value)}/></div>
            <div className="field"><label>Bundle Price ($)</label><input type="number" step="0.01" value={materialCost} onChange={e => setMaterialCost(e.target.value)}/></div>
          </>)}

          <div className="result-panel mt-4"><div className="result-title">Roof Area</div><div className="result-value">{c.area.toFixed(1)} sq ft</div></div>
          <table className="data-table mt-3"><tbody>
            <tr><td>Roofing Squares</td><td className="text-right">{c.squares.toFixed(2)}</td></tr>
            <tr><td>Bundles Needed{tab==='material' ? ` (+${wasteFactor}% waste)` : ''}</td><td className="text-right font-semibold">{c.bundles}</td></tr>
            {tab === 'material' && <tr><td>Estimated Cost</td><td className="text-right font-semibold">${(c.bundles * Number(materialCost)).toFixed(2)}</td></tr>}
            <tr><td>Ridge Rise</td><td className="text-right">{c.rise.toFixed(2)} ft</td></tr>
            <tr><td>Rafter Length</td><td className="text-right">{c.rafter.toFixed(2)} ft</td></tr>
            <tr><td>Roof Angle</td><td className="text-right">{c.angle.toFixed(2)}°</td></tr>
          </tbody></table>
        </div>

        <div className="bg-slate-50 rounded-2xl p-4 flex flex-col items-center">
          <h4 className="font-semibold text-slate-700 mb-3">Roof Diagram (Gable end)</h4>
          <RoofDiagram width={Wft} rise={c.rise} rafter={c.rafter} pitch={pitch} />
          <div className="mt-4 text-xs text-slate-600 text-center max-w-xs">
            Roof pitch <b>{pitch}/12</b> · Rise <b>{c.rise.toFixed(1)} ft</b> · Run <b>{(Wft/2).toFixed(1)} ft</b> · Rafter <b>{c.rafter.toFixed(1)} ft</b>
          </div>
        </div>
      </div>
      )}

      {tab === 'pitch' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="field"><label>Roof Angle (degrees)</label><input type="number" step="0.1" value={angleDeg} onChange={e => setAngleDeg(e.target.value)}/></div>
          </div>
          <div>
            <div className="result-panel"><div className="result-title">Equivalent Pitch</div><div className="result-value">{pitchFromAngle.toFixed(2)} / 12</div></div>
          </div>
        </div>
      )}

      {tab === 'angle' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="field"><label>Pitch (rise per 12")</label><input type="number" step="0.1" value={pitchInput} onChange={e => setPitchInput(e.target.value)}/></div>
          </div>
          <div>
            <div className="result-panel"><div className="result-title">Roof Angle</div><div className="result-value">{angleFromPitch.toFixed(2)}°</div></div>
          </div>
        </div>
      )}

      <StepsPanel steps={[
        `Slope factor = √(1 + (pitch/12)²) = <b>${c.slope.toFixed(4)}</b>`,
        `Roof area = Length × Width × Slope factor = <b>${c.area.toFixed(1)} sq ft</b>`,
        `Squares = area / 100 = <b>${c.squares.toFixed(2)}</b>`,
        `Bundles (≈3 per square) = <b>${c.bundles}</b>`
      ]} />
    </CalcShell>
  );
}

function RoofDiagram({ width, rise, rafter, pitch }) {
  // Scale to fit 320 wide, 220 tall
  const svgW = 340, svgH = 240, pad = 30;
  const scale = Math.min((svgW - 2*pad) / (width || 1), (svgH - 2*pad) / (rise + 4));
  const halfW = (width * scale) / 2;
  const houseH = 60;
  const cx = svgW / 2;
  const baseY = svgH - pad;
  const eaveY = baseY - houseH;
  const peakY = eaveY - rise * scale;

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" className="max-w-md">
      {/* Sky gradient background */}
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e0f2fe" /><stop offset="100%" stopColor="#f8fafc" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width={svgW} height={svgH} fill="url(#sky)" />

      {/* House body */}
      <rect x={cx - halfW} y={eaveY} width={halfW * 2} height={houseH} fill="#f5f5f4" stroke="#78716c" strokeWidth="1.5" />
      {/* Door */}
      <rect x={cx - 10} y={baseY - 30} width="20" height="30" fill="#78350f" />
      {/* Window */}
      <rect x={cx - halfW + 15} y={eaveY + 20} width="24" height="20" fill="#93c5fd" stroke="#1e40af" strokeWidth="1" />
      <rect x={cx + halfW - 39} y={eaveY + 20} width="24" height="20" fill="#93c5fd" stroke="#1e40af" strokeWidth="1" />

      {/* Roof triangle */}
      <polygon points={`${cx - halfW - 8},${eaveY} ${cx + halfW + 8},${eaveY} ${cx},${peakY}`} fill="#9f1239" stroke="#7f1d1d" strokeWidth="1.5" />
      {/* Roof shingle lines */}
      {[0.25, 0.5, 0.75].map(t => (
        <line key={t} x1={cx - halfW - 8 + halfW * t} y1={eaveY - (peakY - eaveY) * -t + (peakY - eaveY) * 0}
          x2={cx + halfW + 8 - halfW * t}
          y2={eaveY - (peakY - eaveY) * -t + (peakY - eaveY) * 0}
          stroke="#7f1d1d" strokeOpacity="0.4" strokeWidth="1" />
      ))}

      {/* Rise measurement */}
      <line x1={cx} y1={peakY} x2={cx} y2={eaveY} stroke="#1e40af" strokeWidth="1.5" strokeDasharray="4 3" />
      <text x={cx + 6} y={(peakY + eaveY) / 2} fontSize="11" fill="#1e40af" fontWeight="700">Rise = {rise.toFixed(1)} ft</text>

      {/* Run measurement (half-width) */}
      <line x1={cx} y1={eaveY + 8} x2={cx + halfW} y2={eaveY + 8} stroke="#059669" strokeWidth="1.5" />
      <line x1={cx} y1={eaveY + 5} x2={cx} y2={eaveY + 11} stroke="#059669" strokeWidth="1.5" />
      <line x1={cx + halfW} y1={eaveY + 5} x2={cx + halfW} y2={eaveY + 11} stroke="#059669" strokeWidth="1.5" />
      <text x={cx + halfW / 2 - 20} y={eaveY + 22} fontSize="11" fill="#059669" fontWeight="700">Run = {(width/2).toFixed(1)} ft</text>

      {/* Rafter (slope) label */}
      <text x={cx + halfW/2 - 30} y={(eaveY + peakY)/2 - 4} fontSize="11" fill="#7c2d12" fontWeight="700" transform={`rotate(${-Math.atan((eaveY - peakY)/halfW) * 180/Math.PI} ${cx + halfW/2} ${(eaveY + peakY)/2})`}>Rafter = {rafter.toFixed(1)} ft</text>

      {/* Pitch label */}
      <g transform={`translate(${cx + halfW * 0.4} ${eaveY - 15})`}>
        <rect x="-4" y="-14" width="60" height="20" rx="4" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1" />
        <text x="26" y="1" textAnchor="middle" fontSize="11" fontWeight="700" fill="#78350f">{pitch} / 12</text>
      </g>
    </svg>
  );
}
